import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { 
  Shield, 
  User, 
  Building, 
  Eye,
  EyeOff,
  MapPin,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUE_AREAS = [
  { id: 'environmental_justice', en: 'Environmental Justice', es: 'Justicia Ambiental' },
  { id: 'voting_rights', en: 'Voting Rights', es: 'Derechos de Voto' },
  { id: 'housing', en: 'Housing', es: 'Vivienda' },
  { id: 'reproductive_freedom', en: 'Reproductive Freedom', es: 'Libertad Reproductiva' },
  { id: 'immigration', en: 'Immigration', es: 'Inmigración' },
  { id: 'workers_rights', en: 'Workers\' Rights', es: 'Derechos Laborales' },
  { id: 'climate_justice', en: 'Climate Justice', es: 'Justicia Climática' },
  { id: 'police_accountability', en: 'Police Accountability', es: 'Responsabilidad Policial' },
  { id: 'education_equity', en: 'Education Equity', es: 'Equidad Educativa' },
  { id: 'healthcare_access', en: 'Healthcare Access', es: 'Acceso a Salud' },
];

const SKILLS = [
  { id: 'legal', en: 'Legal', es: 'Legal' },
  { id: 'media', en: 'Media/Press', es: 'Medios/Prensa' },
  { id: 'research', en: 'Research', es: 'Investigación' },
  { id: 'community_organizing', en: 'Community Organizing', es: 'Organización Comunitaria' },
  { id: 'policy_analysis', en: 'Policy Analysis', es: 'Análisis de Políticas' },
  { id: 'translation', en: 'Translation', es: 'Traducción' },
  { id: 'graphic_design', en: 'Graphic Design', es: 'Diseño Gráfico' },
  { id: 'social_media', en: 'Social Media', es: 'Redes Sociales' },
  { id: 'fundraising', en: 'Fundraising', es: 'Recaudación' },
  { id: 'data_analysis', en: 'Data Analysis', es: 'Análisis de Datos' },
];

const CommunityOnboarding = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    profile_type: 'pseudonymous',
    display_name: '',
    organization_affiliation: '',
    issue_areas: [],
    location_granularity: 'city',
    location_display: '',
    bio: '',
    languages: ['en'],
    skills: [],
    looking_for: [],
    discoverable: true,
    contact_preference: 'in_app',
    show_activity_status: false
  });

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const handleIssueToggle = (issueId) => {
    setFormData(prev => ({
      ...prev,
      issue_areas: prev.issue_areas.includes(issueId)
        ? prev.issue_areas.filter(i => i !== issueId)
        : [...prev.issue_areas, issueId]
    }));
  };

  const handleSkillToggle = (skillId) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skillId)
        ? prev.skills.filter(s => s !== skillId)
        : [...prev.skills, skillId]
    }));
  };

  const handleSubmit = async () => {
    if (formData.issue_areas.length === 0) {
      toast.error(language === 'es' ? 'Selecciona al menos un área de interés' : 'Select at least one issue area');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? '¡Perfil creado!' : 'Profile created!');
      navigate('/community');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al crear perfil' : 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="community-onboarding">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>{language === 'es' ? `Paso ${step} de ${totalSteps}` : `Step ${step} of ${totalSteps}`}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step 1: Profile Type */}
      {step === 1 && (
        <Card data-testid="step-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {language === 'es' ? 'Elige tu Nivel de Visibilidad' : 'Choose Your Visibility Level'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Controla cuánta información compartes con otros'
                : 'Control how much information you share with others'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup 
              value={formData.profile_type} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, profile_type: v }))}
              className="space-y-4"
            >
              <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="anonymous" id="anonymous" />
                <div className="flex-1">
                  <Label htmlFor="anonymous" className="flex items-center gap-2 cursor-pointer">
                    <EyeOff className="h-4 w-4" />
                    {language === 'es' ? 'Anónimo' : 'Anonymous'}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {language === 'es' 
                      ? 'Solo se muestran tus áreas de interés. Ideal para situaciones de alto riesgo.'
                      : 'Only your issue interests are shown. Best for high-risk situations.'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-4 rounded-lg border border-primary/30 bg-primary/5">
                <RadioGroupItem value="pseudonymous" id="pseudonymous" />
                <div className="flex-1">
                  <Label htmlFor="pseudonymous" className="flex items-center gap-2 cursor-pointer">
                    <User className="h-4 w-4" />
                    {language === 'es' ? 'Pseudónimo' : 'Pseudonymous'}
                    <Badge variant="secondary" className="text-xs">{language === 'es' ? 'Recomendado' : 'Recommended'}</Badge>
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {language === 'es' 
                      ? 'Nombre de usuario (no tu nombre real) + ubicación a nivel de ciudad'
                      : 'Display name (not your real name) + city-level location'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="organization" id="organization" />
                <div className="flex-1">
                  <Label htmlFor="organization" className="flex items-center gap-2 cursor-pointer">
                    <Building className="h-4 w-4" />
                    {language === 'es' ? 'Organización' : 'Organization'}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {language === 'es' 
                      ? 'Perfil público completo. Ideal para organizaciones sin fines de lucro establecidas.'
                      : 'Full public profile. Best for established nonprofits.'}
                  </p>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Issue Areas */}
      {step === 2 && (
        <Card data-testid="step-2">
          <CardHeader>
            <CardTitle>
              {language === 'es' ? 'Selecciona tus Áreas de Interés' : 'Select Your Issue Areas'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Elige las causas por las que luchas (selecciona todas las que apliquen)'
                : 'Choose the causes you\'re fighting for (select all that apply)'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {ISSUE_AREAS.map((issue) => (
                <div
                  key={issue.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.issue_areas.includes(issue.id)
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/30'
                  }`}
                  onClick={() => handleIssueToggle(issue.id)}
                  data-testid={`issue-${issue.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={formData.issue_areas.includes(issue.id)}
                      className="pointer-events-none"
                    />
                    <span className="text-sm font-medium">
                      {language === 'es' ? issue.es : issue.en}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {formData.issue_areas.length === 0 && (
              <p className="text-sm text-destructive mt-3">
                {language === 'es' ? 'Selecciona al menos un área' : 'Select at least one area'}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Geographic Interest */}
      {step === 3 && (
        <Card data-testid="step-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              {language === 'es' ? 'Define tu Área Geográfica' : 'Define Your Geographic Area'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Tú controlas qué tan específica es tu ubicación'
                : 'You control how specific your location is'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Nivel de Detalle' : 'Detail Level'}</Label>
              <RadioGroup 
                value={formData.location_granularity} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, location_granularity: v }))}
                className="grid grid-cols-2 gap-2"
              >
                {[
                  { value: 'city', label: language === 'es' ? 'Ciudad' : 'City' },
                  { value: 'county', label: language === 'es' ? 'Condado' : 'County' },
                  { value: 'state', label: language === 'es' ? 'Estado' : 'State' },
                  { value: 'region', label: language === 'es' ? 'Región' : 'Region' },
                ].map((opt) => (
                  <div key={opt.value} className="flex items-center space-x-2 p-3 rounded-lg border">
                    <RadioGroupItem value={opt.value} id={opt.value} />
                    <Label htmlFor={opt.value} className="cursor-pointer">{opt.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">
                {language === 'es' ? 'Tu Ubicación (como se mostrará)' : 'Your Location (as it will be displayed)'}
              </Label>
              <Input
                id="location"
                placeholder={language === 'es' ? 'Ej: Brooklyn, NY o Nueva York' : 'e.g., Brooklyn, NY or New York State'}
                value={formData.location_display}
                onChange={(e) => setFormData(prev => ({ ...prev, location_display: e.target.value }))}
                data-testid="location-input"
              />
              <p className="text-xs text-muted-foreground">
                {language === 'es' 
                  ? 'Nunca rastreamos tu ubicación GPS exacta'
                  : 'We never track your exact GPS location'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Profile Details */}
      {step === 4 && (
        <Card data-testid="step-4">
          <CardHeader>
            <CardTitle>
              {language === 'es' ? 'Detalles del Perfil (Opcional)' : 'Profile Details (Optional)'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Añade más información para conectar mejor con otros'
                : 'Add more info to connect better with others'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.profile_type !== 'anonymous' && (
              <div className="space-y-2">
                <Label htmlFor="display_name">
                  {language === 'es' ? 'Nombre de Usuario' : 'Display Name'}
                </Label>
                <Input
                  id="display_name"
                  placeholder={language === 'es' ? 'Puede ser un pseudónimo' : 'Can be a pseudonym'}
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  data-testid="display-name-input"
                />
              </div>
            )}
            
            {formData.profile_type === 'organization' && (
              <div className="space-y-2">
                <Label htmlFor="org">{language === 'es' ? 'Organización' : 'Organization'}</Label>
                <Input
                  id="org"
                  placeholder={language === 'es' ? 'Nombre de tu organización' : 'Your organization name'}
                  value={formData.organization_affiliation}
                  onChange={(e) => setFormData(prev => ({ ...prev, organization_affiliation: e.target.value }))}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="bio">{language === 'es' ? 'Biografía Breve' : 'Brief Bio'} (200 {language === 'es' ? 'caracteres' : 'chars'})</Label>
              <Textarea
                id="bio"
                placeholder={language === 'es' ? 'Cuéntanos sobre tu trabajo de defensa...' : 'Tell us about your advocacy work...'}
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value.slice(0, 200) }))}
                className="resize-none"
                rows={3}
              />
              <p className="text-xs text-muted-foreground text-right">{formData.bio.length}/200</p>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Habilidades que Ofreces' : 'Skills You Offer'}</Label>
              <div className="flex flex-wrap gap-2">
                {SKILLS.map((skill) => (
                  <Badge
                    key={skill.id}
                    variant={formData.skills.includes(skill.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => handleSkillToggle(skill.id)}
                  >
                    {language === 'es' ? skill.es : skill.en}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Privacy Preferences */}
      {step === 5 && (
        <Card data-testid="step-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {language === 'es' ? 'Preferencias de Privacidad' : 'Privacy Preferences'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Configura quién puede encontrarte y contactarte'
                : 'Set who can find and contact you'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <Label className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  {language === 'es' ? 'Ser Descubrible' : 'Be Discoverable'}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Otros pueden encontrarte en búsquedas' : 'Others can find you in searches'}
                </p>
              </div>
              <Checkbox
                checked={formData.discoverable}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, discoverable: checked }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Método de Contacto' : 'Contact Method'}</Label>
              <RadioGroup 
                value={formData.contact_preference} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, contact_preference: v }))}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="in_app" id="in_app" />
                  <Label htmlFor="in_app">{language === 'es' ? 'Solo en la app (más privado)' : 'In-app only (most private)'}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="email" id="email_pref" />
                  <Label htmlFor="email_pref">{language === 'es' ? 'También por email' : 'Also via email'}</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm">
                {language === 'es' 
                  ? '✓ Puedes cambiar estas configuraciones en cualquier momento en Configuración de Privacidad'
                  : '✓ You can change these settings anytime in Privacy Settings'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/community')}
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'es' ? 'Atrás' : 'Back'}
        </Button>
        
        {step < totalSteps ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={step === 2 && formData.issue_areas.length === 0}
            data-testid="next-btn"
          >
            {language === 'es' ? 'Siguiente' : 'Next'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading} data-testid="complete-btn">
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {language === 'es' ? 'Completar Configuración' : 'Complete Setup'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CommunityOnboarding;
